<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { VAlert, VIcon, VToggleButtonField } from '@/modules/shared/components'
import VSelect from '../../../components/v-select.vue'
import SubscriptionCard from '../../../components/subscription-card.vue'
import AddOnCard from '../../../components/add-on-card.vue'
import type { AddOn, Subscription } from '../../../types'

const props = defineProps<{
  form: any
  v$: any
  subscriptionOptions: Subscription[]
  addOnOptions: AddOn[]
  selectedBillingFrequencyOptions: { label: string; value: string }[]
  isFundConfigDisabled: boolean
  selectedSubscription: Subscription | null
  selectedPlan: Subscription | null
  totalDue: string
  initialValue?: any
}>()

const emit = defineEmits<{
  (e: 'select-subscription', subscription: Subscription): void
  (e: 'select-add-on', addOn: AddOn): void
}>()

const isLegacyPlan = computed(
  () => props.selectedPlan != null && !props.subscriptionOptions.some((s) => s.value === props.selectedPlan?.value),
)

defineExpose({
  isValid: async () => {
    const fields = ['is_venture360_the_administrator', 'selected_services']
    const results = await Promise.all(fields.filter((f) => props.v$[f]).map((f) => props.v$[f].$validate()))
    return results.every(Boolean)
  },
})
</script>

<template>
  <div class="space-y-6">
    <VAlert v-if="isFundConfigDisabled && initialValue" type="alert">
      This Fund has an active billing plan. View
      <RouterLink
        :to="{
          name: 'billing-entity',
          params: { billable_id: initialValue.id, billable_type: initialValue.entity_type },
        }"
        class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
      >
        Billing</RouterLink
      >. Please contact support if you need to make changes.
    </VAlert>

    <VToggleButtonField
      v-model="form.is_venture360_the_administrator"
      label="Is Venture360 the administrator of this fund?"
      :disabled="isFundConfigDisabled"
      property="is_venture360_the_administrator"
      :v$="v$"
    />

    <VToggleButtonField
      v-if="form.is_venture360_the_administrator"
      v-model="form.is_venture360_forming_this_fund"
      label="Is Venture360 forming this fund?"
      :disabled="isFundConfigDisabled"
      property="is_venture360_forming_this_fund"
      :v$="v$"
    />

    <template v-if="form.is_venture360_the_administrator">
      <div id="selected_services">
        <div
          v-if="v$['selected_services']?.$errors[0]?.$message"
          class="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <VIcon name="ExclamationCircle" class="text-red-500" />
          <span>{{ v$['selected_services'].$errors[0].$message }}</span>
        </div>
      </div>

      <VSelect
        v-model="form.selected_billing_frequency"
        label="Please select the billing frequency:"
        :options="selectedBillingFrequencyOptions"
        :disabled="isFundConfigDisabled"
        property="selected_billing_frequency"
        :v$="v$"
      />

      <!-- Legacy plan: show selected plan as read-only card -->
      <template v-if="isLegacyPlan && selectedPlan">
        <SubscriptionCard
          :subscription="selectedPlan"
          :isSelected="true"
          :disabled="true"
          :selectable="false"
          :scaleOnSelect="false"
        />
      </template>

      <!-- Current plans: selectable cards, add-ons, total -->
      <template v-else>
        <div class="grid grid-cols-2 gap-4">
          <SubscriptionCard
            v-for="subscription in subscriptionOptions"
            :key="subscription.title"
            :subscription="subscription"
            :isSelected="selectedSubscription?.value === subscription.value"
            :disabled="isFundConfigDisabled"
            :onClick="() => $emit('select-subscription', subscription)"
          />
        </div>

        <div v-if="addOnOptions.length" class="mt-4 space-y-4">
          <p class="text-sm font-medium text-gray-700">Optional add-ons to pair with your plan</p>
          <AddOnCard
            v-for="addOn in addOnOptions"
            :key="addOn.value"
            :addOn="addOn"
            :disabled="isFundConfigDisabled"
            :isSelected="form.selected_services.includes(addOn.value)"
            :onClick="(a) => $emit('select-add-on', a)"
          />
        </div>

        <div v-if="!isFundConfigDisabled" class="mt-6 flex items-center justify-between border-t pt-4">
          <span class="font-medium text-gray-700">Total Due</span>
          <span class="text-xl font-semibold text-gray-900">{{ totalDue }}</span>
        </div>
        <div v-if="!isFundConfigDisabled" class="text-sm text-gray-500">
          Payment will be setup and processed after you complete all steps.
        </div>
      </template>
    </template>
  </div>
</template>
